import { XMLParser } from "fast-xml-parser";

import { FileRecord, FileService } from "@/shared/domain/files";
import { isAbortError } from "@/shared/errors";
import { HttpClient, HttpResponse } from "@/shared/http";
import { AsyncEvent } from "@/shared/synchronization";

import { LocalFile } from "./LocalFile";
import type { Numeric, Readable } from "./Readable";
import { StorageManager } from "./StorageManager";

/**
 * Determined experimentally
 */
interface S3Error {
  Error: {
    Code: string;
    Message: string;
    Expires: string;
    ServerTime: string;
    RequestId: string;
    HostId: string;
  };
}

/**
 * A range read the object store did not answer with the requested bytes.
 *
 * A read inside a web worker reaches the main thread as a plain `Error` that
 * keeps this error's `name` but not its class or `status` (see
 * `CacheReadError`), so match it there by `name`.
 */
export class RemoteFileReadError extends Error {
  /**
   * The object store's HTTP status when it answered with an error status;
   * `undefined` when it answered with a body that does not match the
   * requested range.
   */
  public readonly status: number | undefined;

  constructor(status: number | undefined, options?: ErrorOptions) {
    super("Failed to read file content", options);
    this.name = "RemoteFileReadError";
    this.status = status;
  }
}

/**
 * A file known by its id, and its byte size when available, without its
 * record: how a read plan names the file behind each scan task.
 */
export interface RemoteFileReference {
  fileId: string;
  sizeBytes?: number | null;
}

function isFileRecord(
  source: FileRecord | RemoteFileReference,
): source is FileRecord {
  return "file_id" in source;
}

/** A file in Roboto's object store, read by byte range through a signed URL. */
export class RemoteFile implements Readable {
  private readonly fileId: string;
  /**
   * The file's record: the one it was built from, or the one fetched for a
   * file built from a reference once its size or version is needed.
   */
  private fileRecord: FileRecord | undefined;
  private fileService: FileService;
  private httpClient: HttpClient;
  private isPresignedUrlLoading = false;
  private presignedUrl: URL | undefined;
  private presignedUrlLoadedEvent = new AsyncEvent();
  /** The size a reference carried, used until the record is fetched. */
  private readonly referenceSizeBytes: number | undefined;

  /**
   * Build from a fetched file record, or from a {@link RemoteFileReference}
   * so that no file-record request is needed to start reading: the signed
   * URL is fetched on the first read, and the record only when the
   * reference carries no size or `toLocalFile` needs the file's version.
   */
  constructor(
    source: FileRecord | RemoteFileReference,
    fileService: FileService,
    httpClient?: HttpClient,
  ) {
    if (isFileRecord(source)) {
      this.fileId = source.file_id;
      this.fileRecord = source;
      this.referenceSizeBytes = undefined;
    } else {
      this.fileId = source.fileId;
      this.fileRecord = undefined;
      this.referenceSizeBytes = source.sizeBytes ?? undefined;
    }
    this.fileService = fileService;
    this.httpClient = httpClient ?? new HttpClient();
  }

  /**
   * `file_id#version` once the file's record is known; the bare file id for
   * a file built from a reference before its record is fetched.
   */
  public id(): string {
    return this.fileRecord === undefined
      ? this.fileId
      : `${this.fileId}#${this.fileRecord.version}`;
  }

  async read(
    offset: Numeric,
    size: Numeric,
    signal?: AbortSignal,
  ): Promise<Uint8Array> {
    const start = Number(offset);
    const numBytesRequested = Number(size);
    const end = start + numBytesRequested - 1; // Range requests are inclusive of bounds

    const url = await this.getPresignedUrl(signal);
    const response = await this.httpClient.get(url, {
      excludeAuth: true,
      ranges: [{ unit: "bytes", start, end }],
      signal,
    });

    if (await this.presignedUrlExpired(response)) {
      signal?.throwIfAborted();

      if (this.presignedUrl === undefined || this.isPresignedUrlLoading) {
        // If a concurrent request has already determined that the presigned URL has expired,
        // avoid stepping on its toes.
        // This AsyncEvent is set when the presigned URL has been successfully refreshed.
        await this.presignedUrlLoadedEvent.wait(signal);
        signal?.throwIfAborted();
      } else {
        // When RemoteFile::getPresignedUrl is called on the next call to RemoteFile::read,
        // it will refresh the presigned URL and set the AsyncEvent.
        this.presignedUrl = undefined;
        this.presignedUrlLoadedEvent.clear();
      }
      return this.read(offset, size, signal);
    }

    if (!response.ok) {
      throw new RemoteFileReadError(response.status, {
        cause: `Object store answered ${response.status} for bytes ${start}-${end} of ${this.fileId}`,
      });
    }

    const bytes = new Uint8Array(await response.arrayBuffer());

    // A range whose end runs past the end of the object is served as the object's
    // remainder, so the body must match the bytes the object holds in range rather
    // than the count requested. Anything shorter was truncated in transit. Returning
    // it would poison the OPFS cache: toLocalFile writes each range at its own offset,
    // so a short range leaves a zero-filled hole in a file that still reaches its
    // expected total size, which the cache's size-only check cannot detect.
    const bytesInRange = Math.min(
      numBytesRequested,
      Math.max(Number(await this.size(signal)) - start, 0),
    );
    if (bytes.byteLength !== bytesInRange) {
      throw new RemoteFileReadError(undefined, {
        cause: `Expected ${bytesInRange} bytes at offset ${start} of ${this.fileId}, received ${bytes.byteLength}`,
      });
    }

    return bytes;
  }

  /**
   * The file's byte size. A file built from a reference that carries no
   * size fetches its record for it, once.
   */
  public async size(signal?: AbortSignal): Promise<bigint> {
    const sizeBytes =
      this.fileRecord?.size ??
      this.referenceSizeBytes ??
      (await this.loadFileRecord(signal)).size;
    return BigInt(sizeBytes);
  }

  /**
   * Request all bytes of the file in batches no larger than 1MB,
   * and write them into a file stored in the Origin Private Filesystem.
   * Return a LocalFile to provide a reader interface for those bytes.
   */
  public async toLocalFile(opts?: {
    path?: LocalFile;
    signal?: AbortSignal;
  }): Promise<LocalFile> {
    const fileRecord =
      this.fileRecord ?? (await this.loadFileRecord(opts?.signal));
    const size = fileRecord.size;
    const file =
      opts?.path ??
      new LocalFile({
        // Default: <file_id>/<vN>/<filename>
        name: getFileRecordFileName(fileRecord),
        parents: [fileRecord.file_id, `v${fileRecord.version}`],
      });

    if (await file.exists(size, opts?.signal)) {
      return file;
    }
    opts?.signal?.throwIfAborted();

    file.lastAccessTime = null;

    try {
      const fileHandle = await file.getWriteableSyncFileHandle(opts?.signal);
      opts?.signal?.throwIfAborted();
      try {
        // Memorialize last access time before downloading bytes.
        // If there is an issue downloading the data,
        // this will ensure the last access time file exists,
        // making it visible for GC.
        await file.memorializeLastAccessTime(opts?.signal);
        opts?.signal?.throwIfAborted();

        const MAX_CHUNK_SIZE = 1024 * 1024; // 1MiB chunks
        const numParts = Math.max(Math.ceil(size / MAX_CHUNK_SIZE), 1);

        // Create array of chunk ranges
        const ranges: { start: number; end: number }[] = [];
        for (let i = 0; i < numParts; i++) {
          ranges.push({
            start: i * MAX_CHUNK_SIZE,
            end: Math.min((i + 1) * MAX_CHUNK_SIZE, size),
          });
        }

        // Fetch all chunks concurrently and write them to the file
        await Promise.all(
          ranges.map(async (range) => {
            opts?.signal?.throwIfAborted();

            const rangeByteLength = range.end - range.start;
            const chunk = await this.read(
              BigInt(range.start),
              BigInt(rangeByteLength),
              opts?.signal,
            );
            const numBytesWritten = fileHandle.write(chunk, {
              at: range.start,
            });
            if (numBytesWritten !== rangeByteLength) {
              throw new Error("Failed to download data, please retry.", {
                cause: `Expected to write ${rangeByteLength} bytes to ${file.toString()}, wrote ${numBytesWritten}`,
              });
            }
          }),
        );
        fileHandle.flush();
        return file;
      } finally {
        fileHandle.close();
      }
    } catch (err: unknown) {
      // Assume any exception downloading the data makes the file unusable
      try {
        await file.delete();
      } catch {
        // swallow, may happen if the file failed to create in the first place
      }

      const isDOMException = err instanceof DOMException;
      if (!isDOMException) {
        // unhandled, rethrow
        throw err;
      }

      // https://developer.mozilla.org/en-US/docs/Web/API/FileSystemSyncAccessHandle/write#exceptions
      if (err.name === "QuotaExceededError") {
        await StorageManager.purgeStorage();
        throw new Error(
          [
            "Failed to download data.",
            "Roboto uses temporary storage on your file system, which is now full.",
            "We've cleared our stored data, but you may need to free additional space.",
            "Check your available storage then reload this panel to retry.",
          ].join(" "),
          {
            cause: err,
          },
        );
      }

      if (isAbortError(err)) {
        throw err;
      }

      // in any other case, just ask the user to retry
      // https://developer.mozilla.org/en-US/docs/Web/API/FileSystemFileHandle/createSyncAccessHandle#exceptions
      throw new Error(
        [
          "Failed to download data.",
          "Roboto uses temporary storage on your file system.",
          "Check your available storage then reload this panel to retry.",
          "You may see this error if using your browser in a 'private browsing' mode.",
        ].join(" "),
        {
          cause: err,
        },
      );
    }
  }

  public toString(): string {
    return `RemoteFile(${this.id()})`;
  }

  private async getPresignedUrl(signal?: AbortSignal): Promise<URL> {
    if (this.isPresignedUrlLoading) {
      await this.presignedUrlLoadedEvent.wait(signal);
    }
    if (this.presignedUrl === undefined) {
      this.isPresignedUrlLoading = true;
      try {
        const url = await this.fileService.getPresignedDownloadUrl(
          this.fileId,
          {
            abortSignal: signal,
          },
        );
        this.presignedUrl = url;
      } finally {
        this.isPresignedUrlLoading = false;
        this.presignedUrlLoadedEvent.set();
      }
    }

    return this.presignedUrl;
  }

  private async loadFileRecord(signal?: AbortSignal): Promise<FileRecord> {
    this.fileRecord = await this.fileService.getFileRecord(this.fileId, {
      abortSignal: signal,
    });
    return this.fileRecord;
  }

  /**
   * Reading of remote files relies on having a presigned url that has not expired.
   * When the URLs expire, AWS returns a 403 with an XML document.
   */
  private async presignedUrlExpired(response: HttpResponse): Promise<boolean> {
    if (response.status !== 403) {
      return false;
    }

    // The signed URL might be expired.
    // AWS returns an XML document that looks like:
    // <Error>
    //    <Code>AccessDenied</Code>
    //    <Message>Request has expired</Message>
    //    <X-Amz-Expires>60</X-Amz-Expires>
    //    <Expires>2024-02-29T18:46:34Z</Expires>
    //    <ServerTime>2024-02-29T18:46:38Z</ServerTime>
    //    <RequestId>BNZ7FV4AWSPFW125</RequestId>
    //    <HostId>...</HostId>
    // </Error>
    const responseText = await response.text();
    const xmlParser = new XMLParser();
    const responseXML = xmlParser.parse(responseText) as S3Error;
    const error = responseXML["Error"];
    if (error === undefined) {
      return false;
    }

    const serverTime = Date.parse(error["ServerTime"] ?? "");
    const expirationTime = Date.parse(error["Expires"] ?? "");
    if (Number.isNaN(serverTime) || Number.isNaN(expirationTime)) {
      throw new RemoteFileReadError(response.status, { cause: responseText });
    }
    return serverTime > expirationTime;
  }
}

function getFileRecordFileName(fileRecord: FileRecord): string {
  const parts = fileRecord.relative_path.split("/");
  return parts[parts.length - 1];
}
